import { Alert, Button, Divider, Form, Input, Row, Typography } from 'antd'
import { Link } from 'react-router-dom'
import { useState } from 'react'
import config from '../../config'
import useTranslation from '../../localization/useTranslation'
import LanguageToggler from '../../components/LanguageToggler/LanguageToggler'
import { getLanguageHeaders } from '../../store/services/http-common'
import appToast from '../../utilities/appToast'

const ForgotPassword = () => {
    const [emailForm] = Form.useForm()
    const [contactForm] = Form.useForm()
    const { t, language } = useTranslation()
    const [loading, setLoading] = useState(false)
    const [contactLoading, setContactLoading] = useState(false)
    const [error, setError] = useState(null)
    const [contactResult, setContactResult] = useState(null)

    const onFinish = async (values) => {
        setLoading(true)
        setError(null)

        try {
            const response = await fetch(`${config.apiUrl}/forgot-password`, {
                method: 'POST',
                body: JSON.stringify({ email: values.email }),
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/json',
                    ...getLanguageHeaders(),
                },
            })

            const data = await response.json()

            if (!response.ok) {
                setError(data?.message ? t(data.message) : t('auth.forgotPassword.failed'))
                return
            }

            appToast.success(data?.message ? t(data.message) : t('auth.forgotPassword.success'))
        } catch (err) {
            setError(err?.message ?? t('auth.forgotPassword.failed'))
        } finally {
            setLoading(false)
        }
    }

    const onLookupContact = async (values) => {
        setContactLoading(true)
        setContactResult(null)

        try {
            const response = await fetch(`${config.apiUrl}/forgot-password/contact`, {
                method: 'POST',
                body: JSON.stringify({ username: values.username }),
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/json',
                    ...getLanguageHeaders(),
                },
            })

            const data = await response.json()

            if (!response.ok) {
                setContactResult({
                    type: 'error',
                    message: data?.message ? t(data.message) : t('auth.forgotPassword.contactFailed'),
                })
                return
            }

            if (data?.data?.has_email) {
                setContactResult({
                    type: 'info',
                    message: t('auth.forgotPassword.hasEmail'),
                })
                return
            }

            const contact = data?.data?.contact
            if (contact?.phone || contact?.whatsapp || contact?.vendor_name) {
                setContactResult({
                    type: 'success',
                    message: t('auth.forgotPassword.contactFound'),
                    contact,
                })
                return
            }

            setContactResult({
                type: 'info',
                message: t('auth.forgotPassword.contactAdminGeneric'),
            })
        } catch (err) {
            setContactResult({
                type: 'error',
                message: err?.message ?? t('auth.forgotPassword.contactFailed'),
            })
        } finally {
            setContactLoading(false)
        }
    }

    return (
        <Row style={{ height: '100vh', position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ position: 'absolute', top: 12, right: 12, zIndex: 1000 }}>
                <LanguageToggler variant="round" />
            </div>

            <div style={{ width: 420, maxWidth: '92%', padding: 20, boxSizing: 'border-box' }}>
                <div style={{ textAlign: 'center', marginBottom: 8 }}>
                    <Typography.Title style={{ marginBottom: 8 }}>{t('auth.forgotPassword.title')}</Typography.Title>
                    <Typography.Paragraph type="secondary">{t('auth.forgotPassword.description')}</Typography.Paragraph>
                </div>

                {error ? <Alert message={error} type="error" showIcon closable style={{ marginBottom: 16 }} onClose={() => setError(null)} /> : null}

                <Form dir={language === 'ar' ? 'rtl' : 'ltr'} form={emailForm} onFinish={onFinish} labelCol={{ span: 24 }}>
                    <Form.Item
                        name="email"
                        label={t('auth.login.email.label')}
                        rules={[
                            { required: true, message: t('auth.login.email.required') },
                            { type: 'email', message: t('auth.login.email.invalid') },
                        ]}
                    >
                        <Input type="email" placeholder={t('auth.login.email.placeholder')} />
                    </Form.Item>
                    <Form.Item>
                        <Button block type="primary" htmlType="submit" loading={loading}>
                            {t('auth.forgotPassword.submit')}
                        </Button>
                    </Form.Item>
                </Form>

                <Divider>{t('auth.forgotPassword.noEmailDivider')}</Divider>

                <Typography.Paragraph type="secondary">
                    {t('auth.forgotPassword.noEmailDescription')}
                </Typography.Paragraph>

                <Form dir={language === 'ar' ? 'rtl' : 'ltr'} form={contactForm} onFinish={onLookupContact} labelCol={{ span: 24 }}>
                    <Form.Item
                        name="username"
                        label={t('users.form.username.label')}
                        rules={[{ required: true, message: t('auth.forgotPassword.usernameRequired') }]}
                    >
                        <Input placeholder={t('users.form.username.placeholder')} autoComplete="username" />
                    </Form.Item>
                    <Form.Item>
                        <Button block htmlType="submit" loading={contactLoading}>
                            {t('auth.forgotPassword.contactAdmin')}
                        </Button>
                    </Form.Item>
                </Form>

                {contactResult ? (
                    <Alert
                        type={contactResult.type}
                        showIcon
                        style={{ marginBottom: 16 }}
                        message={contactResult.message}
                        description={contactResult.contact ? (
                            <div>
                                {contactResult.contact.vendor_name ? (
                                    <div>{t('profile.field.vendorName')}: {contactResult.contact.vendor_name}</div>
                                ) : null}
                                {contactResult.contact.phone ? (
                                    <div>{t('common.phone')}: {contactResult.contact.phone}</div>
                                ) : null}
                                {contactResult.contact.whatsapp ? (
                                    <div>{t('common.whatsapp')}: {contactResult.contact.whatsapp}</div>
                                ) : null}
                            </div>
                        ) : null}
                    />
                ) : null}

                <div style={{ textAlign: 'center' }}>
                    <Link to="/">{t('auth.forgotPassword.backToLogin')}</Link>
                </div>
            </div>
        </Row>
    )
}

export default ForgotPassword
